import { type JSX, useId } from 'react';

import { useTranslate } from '../i18n/useTranslate.js';
import { Button } from '../primitives/Button.js';
import { Card } from '../primitives/Card.js';
import { useOptionalAnalyticsConsent } from './AnalyticsConsentProvider.js';
import { ConsentSaveError } from './ConsentSaveError.js';

/** Persistent member-profile control for a saved or deferred analytics choice. */
export function AnalyticsConsentProfileControl(): JSX.Element | null {
  const t = useTranslate();
  const consent = useOptionalAnalyticsConsent();
  const headingId = useId();

  if (consent === null) {
    return null;
  }

  const { resolution, savingChoice, unsavedChoice, saveError, choose } = consent;
  if (resolution.kind !== 'ready') {
    return null;
  }

  const choice = resolution.current.status;
  const accepted = choice === 'accepted';
  const unknown = choice === 'unknown';
  const withdrawing = savingChoice === 'withdrawn';
  const enabling = savingChoice === 'accepted';
  const retryWithdrawal = accepted && unsavedChoice === 'withdrawn' && saveError !== null;

  return (
    <section data-testid="analytics-consent-profile-control" aria-labelledby={headingId}>
      <Card className="flex max-w-xl flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h3 id={headingId} className="text-lg font-semibold text-foreground">
            {t('analytics.consent.profile.heading')}
          </h3>
          <p className="whitespace-pre-line text-sm text-muted-foreground">
            {t('analytics.consent.notice.body')}
          </p>
        </div>
        {unknown ? null : (
          <div className="flex flex-col gap-1 rounded-lg border border-border bg-muted/50 p-4">
            <p className="text-sm font-semibold text-foreground">
              {t('analytics.consent.profile.currentChoice')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t(
                accepted
                  ? 'analytics.consent.profile.accepted'
                  : 'analytics.consent.profile.disabled',
              )}
            </p>
          </div>
        )}
        <ConsentSaveError error={saveError} />
        <div className={unknown ? 'grid grid-cols-1 gap-3 sm:grid-cols-2' : undefined}>
          <Button
            variant={accepted || unknown ? 'outline' : 'primary'}
            className={unknown ? 'w-full' : undefined}
            disabled={savingChoice !== null}
            onClick={() => void choose(accepted ? 'withdrawn' : 'accepted')}
            aria-describedby={headingId}
          >
            {withdrawing || enabling
              ? t('analytics.consent.notice.saving')
              : retryWithdrawal
                ? t('analytics.consent.profile.retryWithdraw')
                : accepted
                  ? t('analytics.consent.profile.withdraw')
                  : t('analytics.consent.profile.enable')}
          </Button>
          {unknown ? (
            <Button
              variant="outline"
              className="w-full"
              disabled={savingChoice !== null}
              onClick={() => void choose('refused')}
              aria-describedby={headingId}
            >
              {savingChoice === 'refused'
                ? t('analytics.consent.notice.saving')
                : t('analytics.consent.notice.refuse')}
            </Button>
          ) : null}
        </div>
      </Card>
    </section>
  );
}
